<div class="pd-page">

  <app-prevention-group-header activeTab="needs">
    @if (detail(); as d) {
      <div class="period-selector">
        @for (dd of [7, 30, 90]; track dd) {
          <button class="period-btn" [class.active]="periodDays() === dd" (click)="setPeriodDays(dd)">
            {{ 'pilotagePrevention.period.' + dd | translate }}
          </button>
        }
      </div>
    }
  </app-prevention-group-header>

  <div class="breadcrumb-bar">
    <button class="breadcrumb-back" (click)="goBack()">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
      {{ 'opportunites.detail.back' | translate }}
    </button>
    <span class="breadcrumb-sep">·</span>
    <a class="breadcrumb-link" routerLink="/pilotage-prevention/besoins">{{ 'pilotagePrevention.nav.needsCoverage' | translate }}</a>
    @if (detail(); as d) {
      <span class="breadcrumb-sep">·</span>
      <span class="breadcrumb-current">{{ d.subjectLabel }}</span>
    }
  </div>

  @if (notFound()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'opportunites.detail.states.notFound' | translate }}</div>
  } @else if (loadError()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'opportunites.states.error' | translate }}</div>
  } @else if (!detail()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'opportunites.states.loading' | translate }}</div>
  } @else if (detail(); as d) {

    <!-- Titre = subjectLabel fourni par l'API (jamais la clé du sujet, jamais son origine) -->
    <div class="card" style="margin-bottom:16px">
      <div class="pd-title">{{ d.subjectLabel }}</div>
      @if (d.noData) {
        <div class="cs" style="margin-top:4px">{{ 'opportunites.detail.states.noData' | translate }}</div>
      } @else if (d.insufficientData) {
        <div class="cs" style="margin-top:4px">{{ 'opportunites.states.insufficientData' | translate }}</div>
      } @else {
        <div class="cs" style="margin-top:4px">
          {{ (d.detectedSessions! > 1 ? 'opportunites.detail.header.identifiedPlural' : 'opportunites.detail.header.identifiedSingular')
              | translate: { count: d.detectedSessions, days: d.days } }}
        </div>
      }
    </div>

    @if (!d.noData) {
      <!-- KPI : Opportunités identifiées / Couvertes / Encore sans réponse / Couverture -- valeurs et
           taux fournis par l'API (jamais recalculés) ; `insufficientData` : « Données insuffisantes »,
           aucun nombre. -->
      <div class="card" style="margin-bottom:16px">
        <div class="kpi-grid">
          <div class="kpi-card" [class.kpi-card--empty]="d.detectedSessions == null">
            <div class="kpi-label">{{ 'opportunites.detail.kpi.identified' | translate }}</div>
            <div class="kpi-value" [class.kpi-value--muted]="d.detectedSessions == null">{{ formatCount(d.detectedSessions) }}</div>
          </div>
          <div class="kpi-card" [class.kpi-card--empty]="d.coveredCount == null">
            <div class="kpi-label">{{ 'opportunites.detail.kpi.covered' | translate }}</div>
            <div class="kpi-value" [class.kpi-value--muted]="d.coveredCount == null">{{ formatCount(d.coveredCount) }}</div>
          </div>
          <div class="kpi-card" [class.kpi-card--empty]="d.uncoveredCount == null">
            <div class="kpi-label">{{ 'opportunites.detail.kpi.uncovered' | translate }}</div>
            <div class="kpi-value" [class.kpi-value--muted]="d.uncoveredCount == null">{{ formatCount(d.uncoveredCount) }}</div>
            @if (d.uncoveredSiteCount != null && d.uncoveredSiteCount > 0) {
              <div class="kpi-sub">{{ (d.uncoveredSiteCount > 1 ? 'opportunites.detail.kpi.uncoveredSitesPlural' : 'opportunites.detail.kpi.uncoveredSitesSingular') | translate: { count: d.uncoveredSiteCount } }}</div>
            }
          </div>
          <div class="kpi-card" [class.kpi-card--empty]="d.coverageRate == null">
            <div class="kpi-label">{{ 'opportunites.detail.kpi.coverage' | translate }}</div>
            <div class="kpi-value" [class.kpi-value--muted]="d.coverageRate == null">
              {{ d.insufficientData ? formatRate(d.coverageRate) : formatResolvedRate(d.coverageRate) }}
            </div>
          </div>
        </div>
      </div>
    }

    @if (!d.noData && !d.insufficientData) {
      @if (fullyCovered()) {
        <div class="card" style="margin-bottom:16px">
          <div class="fully-covered">{{ 'opportunites.detail.states.fullyCovered' | translate }}</div>
        </div>
      } @else if (reasons().length > 0) {
        <!-- Raisons : libellé indexé par le CODE fourni par l'API, ordre et ACTION fournis par l'API.
             Aucun CTA pour une action `NONE` (ex. « Contenu en cours de validation ») : la raison
             s'affiche sans bouton. -->
        <div class="card" style="margin-bottom:16px">
          <div class="ct" style="margin-bottom:14px">{{ 'opportunites.detail.reasons.title' | translate }}</div>
          @for (r of reasons(); track r.reason) {
            <div class="reason-row">
              <div class="reason-info">
                <span class="reason-label" [attr.title]="gapReasonTooltip(r.reason)">{{ gapReasonLabel(r.reason) }}</span>
                <span class="reason-volume">{{ reasonVolumeLabel(r) }}</span>
              </div>
              @if (hasAction(r.action)) {
                <button type="button" class="reason-cta-link" (click)="onActionClick(r.action)">{{ actionLabel(r.action) }} →</button>
              }
            </div>
          }
        </div>
      }

      <!-- Quels sites sont les plus concernés ? Si TOUTES les lignes sont masquées (petite cohorte), un
           état simple remplace un tableau plein de « Données insuffisantes » (bruit sans information). -->
      <div class="card" style="margin-bottom:16px">
        <div class="ct" style="margin-bottom:14px">{{ 'opportunites.detail.sites.title' | translate }}</div>
        @if (allSitesMasked()) {
          <div class="cs" style="text-align:center;padding:20px 0">
            {{ 'opportunites.detail.sites.collecting' | translate }}<br>
            {{ 'opportunites.detail.sites.collectingSubtitle' | translate }}
          </div>
        } @else if (d.sites.length > 0) {
          <div class="site-table">
            <div class="site-row site-row--header">
              <span>{{ 'opportunites.detail.sites.columns.site' | translate }}</span>
              <span>{{ 'opportunites.detail.sites.columns.detected' | translate }}</span>
              <span>{{ 'opportunites.detail.sites.columns.coverage' | translate }}</span>
              <span>{{ 'opportunites.detail.sites.columns.uncovered' | translate }}</span>
              <span>{{ 'opportunites.detail.sites.columns.topGap' | translate }}</span>
              <span>{{ 'opportunites.detail.sites.columns.action' | translate }}</span>
            </div>
            @for (site of d.sites; track trackSite($index, site)) {
              <div class="site-row site-row--data">
                <span class="site-row-name">{{ site.establishmentName }}</span>
                @if (site.insufficientData) {
                  <span class="site-row-masked">{{ 'opportunites.states.insufficientData' | translate }}</span>
                } @else {
                  <span [attr.data-label]="'opportunites.detail.sites.columns.detected' | translate">{{ formatCount(site.detectedSessions) }}</span>
                  <span [attr.data-label]="'opportunites.detail.sites.columns.coverage' | translate">{{ formatResolvedRate(site.coverageRate) }}</span>
                  <span [attr.data-label]="'opportunites.detail.sites.columns.uncovered' | translate">{{ formatCount(site.uncoveredCount) }}</span>
                  <span [attr.data-label]="'opportunites.detail.sites.columns.topGap' | translate" [attr.title]="gapReasonTooltip(site.topGapReason)">
                    {{ site.topGapReason ? gapReasonLabel(site.topGapReason) : '—' }}
                  </span>
                  <span>
                    @if (hasAction(site.topGapAction)) {
                      <button type="button" class="reason-cta reason-cta--compact" (click)="onActionClick(site.topGapAction)">{{ actionLabel(site.topGapAction) }}</button>
                    } @else {
                      —
                    }
                  </span>
                }
              </div>
            }
          </div>
        } @else {
          <div class="cs" style="text-align:center;padding:20px 0">{{ 'opportunites.detail.sites.empty' | translate }}</div>
        }
      </div>
    }

    <!-- Offres liées -- catalogue courant, TOUJOURS affichées (même sur noData/insufficientData :
         indépendantes des sessions), exactement celles du contrat (nom + sites actifs + CTA existant
         de gestion/déploiement), sans prix et sans filtre côté front. 3 offres visibles par défaut
         (tri par sites actifs décroissants), « Voir les X autres » développe la liste complète. -->
    <div class="card">
      <div class="ct">{{ 'opportunites.detail.offers.title' | translate }}</div>
      <div class="cs" style="margin-bottom:14px">{{ 'opportunites.detail.offers.note' | translate }}</div>
      @if (d.linkedOffers.length > 0) {
        <div class="offer-list">
          @for (offer of visibleOffers(); track trackOffer($index, offer)) {
            <div class="offer-row">
              <span class="offer-row-name">{{ offer.offerName }}</span>
              <span class="offer-row-sites">{{ 'opportunites.detail.offers.sitesActive' | translate: { count: offer.activeSiteCount } }}</span>
              <button type="button" class="reason-cta" (click)="onOfferCtaClick(offer)">{{ offerCtaLabel(offer) }}</button>
            </div>
          }
        </div>
        @if (hiddenOffersCount() > 0) {
          <button type="button" class="see-more-btn" (click)="showMoreOffers()">
            {{ 'opportunites.detail.offers.seeMore' | translate: { count: hiddenOffersCount() } }}
          </button>
        }
      } @else {
        <div class="cs" style="text-align:center;padding:20px 0">{{ 'opportunites.detail.offers.empty' | translate }}</div>
      }
    </div>
  }
</div>
